"use client";

import React from "react";
import { motion, Variants } from "framer-motion";
import { FileText, Calendar, ShieldAlert, BadgeCheck, FileWarning, HelpCircle } from "lucide-react";
import Link from "next/link";

export function TermsContent() {
  const containerVariants: Variants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants: Variants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15,
      },
    },
  };

  const sections = [
    {
      icon: BadgeCheck,
      title: "1. Acceptance of Terms",
      text: "By accessing and using the CCMT College Finder website, you agree to be bound by these Terms & Conditions, all applicable laws, and regulations. If you do not agree with any of these terms, you are prohibited from using or accessing this site.",
    },
    {
      icon: ShieldAlert,
      title: "2. Official Affiliation Disclaimer",
      text: "This website is an unofficial, student-led, and open-source project. We are NOT affiliated with, authorized, or endorsed by the Centralized Counseling for M.Tech./M.Arch./M.Plan. (CCMT), NITs, IIITs, GFTIs, or any government body. All official procedures must be conducted on the official CCMT portal.",
    },
    {
      icon: FileWarning,
      title: "3. Prediction & Cutoff Accuracy Disclaimer",
      text: "The data displayed on this site, including opening/closing scores and admission probability predictions, is compiled from public archives and historical cutoff lists of past years (2021-2026). While we strive to maintain high-fidelity representations of historical trends, cutoffs for the current year depend on exam difficulty, candidate distributions, and choice matrix shifts. We make no warranty regarding the absolute accuracy of predictions. Always double-check official counseling brochures.",
    },
    {
      icon: HelpCircle,
      title: "4. Limitation of Liability",
      text: "Under no circumstances shall the creators, developers, or contributors of CCMT College Finder be held liable for any damages, errors, or losses arising out of the use or inability to use this platform. You assume full responsibility for all decisions related to your choice-filling preferences and admissions counseling process.",
    },
  ];

  return (
    <div className="min-h-screen bg-background selection:bg-primary/30 pt-10 pb-20 overflow-hidden">
      {/* Decorative glows */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-7xl h-full -z-10 pointer-events-none">
        <div className="absolute top-[-5%] left-[-10%] w-[50%] h-[40%] bg-primary/10 blur-[130px] rounded-full opacity-60" />
        <div className="absolute bottom-[10%] right-[-10%] w-[50%] h-[40%] bg-secondary/10 blur-[130px] rounded-full opacity-60" />
      </div>

      <div className="container mx-auto px-6 max-w-4xl">
        {/* Hero Section */}
        <section className="text-center py-16">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-surface-container/50 border border-outline-variant/10 text-primary text-sm font-bold mb-6 shadow-sm backdrop-blur-md"
          >
            <FileText className="size-4 text-amber-500" />
            <span>Usage Agreement</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="text-3xl md:text-5xl font-display font-black tracking-tight text-foreground mb-6 leading-tight"
          >
            Terms & Conditions
          </motion.h1>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="inline-flex items-center justify-center gap-1.5 text-xs font-semibold text-on-surface-variant bg-surface-container-low px-3 py-1 rounded-full border border-outline-variant/10"
          >
            <Calendar className="size-3.5" />
            <span>Last Updated: June 1, 2026</span>
          </motion.div>
        </section>

        {/* Terms Body */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="space-y-6"
        >
          {sections.map((section, index) => {
            const Icon = section.icon;
            return (
              <motion.div
                key={index}
                variants={itemVariants}
                className="p-6 md:p-8 rounded-3xl bg-surface-container border border-outline-variant/20 shadow-sm flex flex-col md:flex-row gap-6 items-start"
              >
                <div className="flex-shrink-0 size-12 rounded-2xl bg-primary/10 text-primary flex items-center justify-center">
                  <Icon className="size-6" />
                </div>
                <div className="space-y-2">
                  <h3 className="font-display font-bold text-lg md:text-xl text-foreground">
                    {section.title}
                  </h3>
                  <p className="text-on-surface-variant text-sm md:text-base leading-relaxed text-pretty">
                    {section.text}
                  </p>
                </div>
              </motion.div>
            );
          })}

          {/* Feedback Card */}
          <motion.div
            variants={itemVariants}
            className="p-6 md:p-8 rounded-3xl bg-primary/5 border border-primary/20 shadow-sm flex flex-col md:flex-row justify-between items-center gap-6"
          >
            <div className="space-y-2 text-center md:text-left">
              <h3 className="font-display font-bold text-lg md:text-xl text-foreground">
                Inquiries & Feedback
              </h3>
              <p className="text-on-surface-variant text-sm max-w-lg">
                Have questions regarding our terms, or noticed an error in our dataset? Feel free to reach out to us by email.
              </p>
            </div>
            <Link
              href="mailto:py.assistance@hotmail.com"
              className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-primary text-primary-foreground font-bold text-sm hover:scale-105 active:scale-95 transition-all shadow-md"
            >
              <span>Contact Team</span>
            </Link>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
}
